/**
 * Caches the result of a function call. If the function is called again with the same key,
 * the cached result is returned instead of calling the function again.
 * This is useful for expensive functions that are called frequently with the same arguments.
 * @param {*} fn The function to cache.
 * @param {*} keyFn A function that generates a cache key from the arguments of the cached function. If not provided, all calls will use the same cache entry.
 * @returns A cached version of the function.
 * @example
 * const myAPICall = cached(() => fetch('/endpoint').then(r => r.json()));
 * const res1 = await myAPICall(); // actually calls the api
 * const res2 = await myAPICall(); // returns a resolved promise, so instantly goes through
 * // res1 === res2
 * @example
 * // Using a key function to cache based on arguments
 * const getColorValue = cached(color => color, color => color);
 * const blue = getColorValue('blue'); // returns 'blue'
 * const red = getColorValue('red'); // returns 'red'
 * @example 
 * // No key function means all calls share the same cache entry
 * const getColorValue = cached(color => color);
 * const blue = getColorValue('blue'); // returns 'blue'
 * const red = getColorValue('red'); // returns 'blue'
 */
export default function cached(fn, keyFn = () => null) {
	const cache = new Map();
	let clearTimer;

	const cachedFunc = function() {
		const key = keyFn.apply(this, arguments);
		if (!cache.has(key)) {
			cache.set(key, fn.apply(this, arguments));
		}
		return cache.get(key);
	};

	/**
	 * Sets a timeout to clear the entire cache after the given number of milliseconds.
	 * @param {*} ms The number of milliseconds to wait before clearing the cache.
	 */
	cachedFunc.expireIn = (ms) => {
		clearTimer = setTimeout(() => this.reset(), ms);
	};

	/**
	 * Clears the entire cache and cancels any pending expiration timer.
	 */
	cachedFunc.reset = () => {
		clearTimeout(clearTimer);
		cache.clear();
	};

	/**
	 * Forces a refresh of the cache for the given arguments by clearing the cache entry
	 * and calling the function again.
	 * @param  {...any} args The arguments to force refresh for.
	 * @returns The result of calling the function again with the given arguments.
	 */
	cachedFunc.force = function() {
		const key = keyFn.apply(this, arguments);
		cachedFunc.delete(key);
		return cachedFunc.apply(this, arguments);
	};

	return cachedFunc;
}
